/**
 * §3: the calendar a seizoen is built from - the seizoen, its wedstrijddagen, the banen on a
 * dag, the speelschema those banen run, and what the boekje prints.
 */
export const wedstrijden = {
  seizoenen: {
    label: "Seizoenen",
    seizoen: "Seizoen",
    periode: "Periode",
    periodeWaarde: "{{start}} - {{eind}}",
    leeg: "Er is nog geen seizoen ingericht.",
    terug: "Alle seizoenen",
    kies: "Kies een seizoen om het te wijzigen, of maak er een aan.",
    /** The column header is screen-reader only: a sighted beheerder reads the badge itself. */
    geselecteerdKolom: "Geselecteerd voor andere schermen",
    geselecteerdBadge: "Geselecteerd",
    aanmaken: "Seizoen aanmaken",
    aanmakenUitleg:
      "Een naam en twee datums. Niveaus, klassen en functies gelden voor elk seizoen, dus die vul je hier niet in.",
    naam: "Naam",
    naamPlaceholder: "2027",
    start: "Startdatum",
    eind: "Einddatum",
    datumsUitleg:
      "De datums geven aan wanneer het seizoen loopt. Je kunt ook een wedstrijddag buiten deze datums toevoegen. Die dag krijgt dan een waarschuwing, maar blijft gewoon staan.",
    naarDagen: "Wedstrijddagen beheren",
    verwijderenTitle: "Seizoen verwijderen",
    verwijderenUitleg:
      "Verwijderen kan alleen zolang het seizoen nog leeg is. Hangen er wedstrijddagen, ploegen, inschrijfperiodes of een planning onder? Hernoem het seizoen dan in plaats van het te verwijderen.",
    verwijderenVraag: "Seizoen {{naam}} verwijderen?",
    verwijderenDescription:
      "Je kunt dit niet ongedaan maken. Staat er al iets in het seizoen? Dan kun je het seizoen niet verwijderen.",
    verwijderenConfirm: "Seizoen verwijderen",
  },

  dagen: {
    label: "Wedstrijddagen",
    datum: "Datum",
    organiserendKorps: "Organiserend korps",
    niveau: "Niveau",
    /** Said out loud rather than left blank: an empty cell reads as a name that failed to load. */
    nogNietBekend: "Nog niet bekend",
    nogGeenKorps: "nog geen organiserend korps",
    zoek: "Zoek op korps of niveau",
    filterNiveau: "Niveau",
    filterDatum: "Datum",
    leeg: "Dit seizoen heeft nog geen wedstrijddagen.",
    terug: "Alle wedstrijddagen",
    kies: "Kies een wedstrijddag om die te wijzigen of er banen aan toe te voegen.",
    toevoegen: "Wedstrijddag toevoegen",
    aanSeizoen: "Aan seizoen {{naam}}.",
    submitToevoegen: "Toevoegen",
    titel: "{{datum}} · {{korps}}",
    buitenSeizoen: "Valt buiten seizoen {{naam}}.",
    verwijderenTitle: "Wedstrijddag verwijderen",
    verwijderenUitleg:
      "Je kunt een wedstrijddag verwijderen zolang niemand de dag gebruikt. Heeft een korps de dag in zijn voorkeuren, heeft een ondersteuner zich opgegeven of staat er een planning op? Dan blijft de dag staan.",
    verwijderenVraag: "Wedstrijddag {{datum}} verwijderen?",
    verwijderenDescription:
      "De banen op deze dag verdwijnen ook. Je kunt dit niet ongedaan maken.",
    verwijderenConfirm: "Wedstrijddag verwijderen",
  },

  dagForm: {
    datum: "Datum",
    niveau: "Niveau",
    /** A warning and not a rule: §3 lets the beheerder enter the dagen. */
    buitenSeizoen:
      "Deze datum valt buiten seizoen {{naam}}. Je kunt de dag toch opslaan, maar controleer eerst of de datum klopt.",
    geenKorps:
      "Zonder organiserend korps heeft deze dag nog geen meldadres. Kies een organiserend korps. Het meldadres wordt dan het adres van dat korps.",
    meldadres: "Meldadres",
    meldadresUitleg:
      "Het adres waar bezoekers zich melden. Het staat vooraf in het wedstrijdboekje. Het adres komt standaard van het organiserende korps. Houd je de wedstrijd op een ander terrein? Vul dan het adres van dat terrein in.",
    adres: "Adres",
    postcode: "Postcode",
    plaats: "Plaats",
    overnemenTitle: "Meldadres overnemen?",
    overnemenDescription:
      "Het meldadres staat nu op {{adres}}, {{postcode}} {{plaats}}. Wil je het adres van {{korps}} overnemen, of laten staan omdat de wedstrijd ergens anders wordt gehouden?",
    overnemenLaten: "Laten staan",
    overnemenActie: "Adres overnemen",
    duplicaatTitle: "Mogelijk een dubbele wedstrijddag",
    duplicaatAanpassen: "Aanpassen",
    duplicaatDoorgaan: "Toch doorgaan",

    /**
     * Create path only: JBNL fixes the datums and the klassen per dag together in the autumn.
     * Corrections go through the Wedstrijdbanen card, which can explain the renumbering.
     * See docs/adr/0030-a-wedstrijddag-is-a-korps-and-a-meldadres.md.
     */
    banen: "Wedstrijdbanen",
    banenUitleg:
      "De banen van deze dag, per klasse. Zijn er twee banen van dezelfde klasse? Voeg de klasse dan twee keer toe. Later kun je bij de wedstrijddag zelf nog banen toevoegen.",
    banenLijstLabel: "Banen op deze nieuwe dag",
    banenOnbekend: "Onbekende klasse",
    baanKlasse: "Klasse",
    baanToevoegen: "Baan toevoegen",
    baanVerwijderKnop: "{{baan}} weer weghalen",

    /** The two boekje fields already known once the dag is assigned; the other three are not. */
    wedstrijdleiders: "Wedstrijdleiders",
    wedstrijdleidersUitleg:
      "De wedstrijdleider en het telefoonnummer staan meestal al vast als de dag een organiserend korps heeft. De briefing, de meldtijd en de opmerkingen vul je later in, bij de wedstrijddag zelf.",
    kiesWedstrijdleiderKorpsEerst: "Kies eerst een organiserend korps.",
  },

  korpsKiezer: {
    legend: "Organiserend korps",
    nogNietBekend: "Nog niet bekend",
    wissen: "Wissen",
    korps: "Korps",
    plaats: "Plaats",
    zoek: "Zoek op korps of plaats",
    leeg: "Er zijn nog geen korpsen uit het JBNL Portaal opgehaald. Start een synchronisatie bij Synchronisaties.",
  },

  /**
   * Four states, and the tone is the point: `bevestigd` and `korpsAdres` are finished and read
   * as confirmations, while `zonderPunt` and `nietGekozen` name somewhere the reistijd will not
   * be measured from. See docs/adr/0013-reistijd-computation.md and
   * docs/adr/0060-a-meldadres-is-chosen-not-typed.md.
   */
  meldadresZoeker: {
    label: "Adres zoeken",
    placeholder: "Typ straat en huisnummer, bijvoorbeeld Damrak 1 Amsterdam",
    korpsAdres:
      "Dit is de kazerne van het organiserende korps. De Wedstrijdplanner meet de reistijden naar deze wedstrijddag vanaf dit adres.",
    zonderPunt:
      "Dit adres staat nog niet op de kaart. De Wedstrijdplanner meet de reistijden naar deze wedstrijddag daarom vanaf het adres dat het JBNL Portaal van {{korps}} heeft. Is de wedstrijd echt op dit adres? Zoek het adres dan hierboven op.",
    nietGekozen:
      "Dit adres is met de hand ingevuld en niet uit de zoeklijst gekozen. Daarom staat het nog niet op de kaart. Tot dan meet de Wedstrijdplanner de reistijden vanaf het adres van {{korps}}. Is de wedstrijd echt op dit adres? Zoek het adres dan hierboven op.",
    bevestigd:
      "Dit adres is gekozen en vastgelegd. De Wedstrijdplanner meet de reistijden naar deze wedstrijddag vanaf dit adres.",
    nietGevonden:
      "Geen adres gevonden. Controleer de spelling, of vul de velden hieronder zelf in.",
  },

  banen: {
    title: "Wedstrijdbanen",
    leeg: "Deze wedstrijddag heeft nog geen banen. Voeg per klasse een baan toe.",
    lijstLabel: "Banen op deze dag",
    klasse: "Klasse",
    toevoegen: "Baan toevoegen",
    verwijderKnop: "{{baan}} verwijderen",
    verwijderVraag: "{{baan}} verwijderen?",
    verwijderDescription:
      "De Wedstrijdplanner nummert de andere banen van deze klasse opnieuw. Hun naam kan dus veranderen.",
    verwijderConfirm: "Baan verwijderen",
    uitleg:
      "Je kunt banen wijzigen zolang het niveau van de wedstrijddag nog concept is, ook nadat de inschrijfperiode is gesloten. Een baan waarop al een ploeg of jurylid staat, kun je niet meer verwijderen.",
  },

  boekje: {
    title: "Wedstrijdboekje",
    briefing: "Briefing",
    briefingUitleg:
      'Eén regel, zoals het boekje die afdrukt. Bijvoorbeeld: "Briefing juryleden 08:00, ploegleiders 08:15".',
    opmerkingen: "Opmerkingen",
    opmerkingenUitleg:
      "Eén regel per opmerking, bijvoorbeeld een rijroute of een mededeling. Lege regels vallen weg. Promoties zet je op het tabblad Promotieplaatsen. Die komen in het boekje vanzelf boven de opmerkingen te staan.",
    wedstrijdleider: "Wedstrijdleider",
    wedstrijdleiderUitleg:
      "Wie deze dag leidt, zoals het boekje de namen afdrukt. Bij meer dan één naam zet je ze achter elkaar, gescheiden door een komma.",
    meldtijd: "Meldtijd",
    meldtijdUitleg:
      'De regel onder het meldadres. In 2026 stond er op elke dag "Minimaal één uur voor aanvang wedstrijd". Laat je dit leeg? Dan drukt het boekje niets af.',
    telefoon: "Telefoonnummer wedstrijdleider",
    telefoonUitleg:
      "Het nummer (of de nummers) waarop de wedstrijdleiders díe dag bereikbaar zijn. Het komt in het boekje te staan. Vul dus geen privénummer in van iemand die er niet is.",
    kiesWedstrijdleider: "Kies wedstrijdleider",
    kiesWedstrijdleiderTitel: "Wedstrijdleiders",
    geenBeheerders: "Geen beheerders gevonden.",
    geenTelefoonnummer: "Geen telefoonnummer bekend",
    reistijdOnbekend: "Reistijd onbekend",
    wedstrijdleiderInvullen: "Invullen",
    standaardtekstenLabel: "Veelgebruikt:",
    /** Not a bare "Opslaan": the wedstrijddag's own form sits on the same screen with one. */
    opslaan: "Boekjegegevens opslaan",
  },

  /** Shorter than the card headings below: four labels share one strip (ADR-0064). */
  tabs: {
    strip: "Onderdelen van deze wedstrijddag",
    banen: "Banen",
    boekje: "Boekje",
    promoties: "Promotieplaatsen",
    speelschema: "Speelschema",
    ploegen: "Ploegen",
    ondersteuners: "Ondersteuners",
  },

  promoties: {
    title: "Promotieplaatsen",
    uitleg:
      "Welke plaatsen van een baan naar een latere wedstrijddag gaan, en naar welke dag. Het boekje zet dit zelf in een regel onder de meldtijd. Je vult dit lang voor de wedstrijden in. Daarom noem je een plaats en nooit een ploeg.",
    /** Nothing to point at: a dag with no later dag in the seizoen cannot announce a promotie. */
    geenBestemming:
      "Er staat geen latere wedstrijddag in dit seizoen. Een plaats kan dus nog nergens naartoe.",
    geen: "Nog geen promotieplaatsen op deze baan.",
    plaats: "Plaats",
    gaatNaar: "gaat naar",
    kies: "Kies een wedstrijddag",
    bestemmingVeld: "Bestemming van plaats {{plaats}}",
    verwijderPlaats: "Plaats {{plaats}} verwijderen",
    toevoegen: "Plaats toevoegen",
    kiesBestemming: "Kies bij elke plaats een wedstrijddag.",
    zonderKlasse_one: "Op deze wedstrijddag staat nog geen baan {{klasse}}.",
    zonderKlasse_other:
      "Op deze {{count}} wedstrijddagen staat nog geen baan {{klasse}}.",
    zonderKlasseUitleg:
      "Een finale krijgt zijn banen vaak later, dus je kunt de promotieplaatsen nu al opslaan. Controleer wel of de banen er staan voordat het boekje uitkomt.",
    nietOpgeslagen:
      "Nog niet opgeslagen. Het boekje drukt dit pas zo af als je opslaat.",
    inHetBoekje: "Zo komt het in het boekje:",
    opslaan: "Promotieplaatsen opslaan",
  },

  speelschema: {
    title: "Speelschema",
    caption: "Speelschema per baan",
    uitleg:
      "Laat een vakje leeg om er een ploeg in te kunnen delen. Vul je een vakje in, bijvoorbeeld met een <em>Pauze</em> of een <em>Herdenking</em>? Dan gebeurt dat op die tijd. Er kan dan geen ploeg meer bij. Het aantal lege vakjes is het aantal ploegen dat op de baan past.",
    /** The banen follow as a list, so the heading stays readable on a dag that has four. */
    afwijkend_one: "Deze baan wijkt af van het standaardspeelschema:",
    afwijkend_other: "Deze banen wijken af van het standaardspeelschema:",
    afwijkendUitleg:
      "Zodra een baan bestaat, bepaal jij de tijden. Met Standaard herstellen zet je een baan terug naar het standaardspeelschema.",
    tijd: "Tijd",
    tijdVeld: "Tijd {{tijd}}",
    plekken_one: "{{count}} plek",
    plekken_other: "{{count}} plekken",
    vrij: "Vrij voor een ploeg",
    cel: "{{baan}} om {{tijd}}",
    celWeghalen: "{{baan}} om {{tijd}} weghalen",
    celToevoegen: "{{baan}} om {{tijd}} toevoegen",
    toevoegen: "Toevoegen",
    rijToevoegen: "Tijd toevoegen",
    vuil: "Niet opgeslagen wijzigingen.",
    opslaan: "Speelschema opslaan",
    herstellen: "Standaard herstellen voor {{baan}}",
    herstelVraag: "Standaardspeelschema herstellen voor {{baan}}?",
    herstelDescription:
      "Het standaardspeelschema voor deze klasse op dit niveau vervangt de tijden van deze baan.",
    herstelConfirm: "Standaard herstellen",
    geenStandaard:
      "Voor {{banen}} is op dit niveau geen standaardspeelschema ingesteld. Zonder standaard begint een baan leeg. Een lege baan is hier geen fout: Brandbestrijding speelt bijvoorbeeld geen halve finale.",
  },

  errors: {
    niveaus: "Kon de niveaus niet ophalen.",
    klassen: "Kon de klassen niet ophalen.",
    korpsen: "Kon de korpsen niet ophalen.",
    dagen: "Kon de wedstrijddagen niet ophalen.",
    aanmaken: "Aanmaken is mislukt.",
    opslaan: "Opslaan is mislukt.",
    verwijderen: "Verwijderen is mislukt.",
    speelschemaOpslaan: "Speelschema opslaan is mislukt.",
    herstellen: "Herstellen is mislukt.",
    baanToevoegen: "Baan toevoegen is mislukt.",
    baanVerwijderen: "Baan verwijderen is mislukt.",
    promotiesOpslaan: "Promotieplaatsen opslaan is mislukt.",
    wedstrijdleiderKandidaten: "Kon de wedstrijdleiders niet ophalen.",
  },
} as const;
